import { CrossUI } from 'phx-core';

import { Component, EventEmitter, Output, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { CTA_ACTION_TYPES, EXPANSION_UPSELL_TYPES, ExpansionUpsellService } from '../../service';
import { NzMessageService } from 'ng-zorro-antd';
import { FeatureUpgradeService } from '@misc/upsell/upgrade.service';

import { ExpansionUpsellConfig, IExpansionUpsellsCompanyPageUpsell, IExpansionUpsellsData, IUpsellCTA } from '../../interface';
import { Session } from '@services/session';

type UpsellType = keyof typeof EXPANSION_UPSELL_TYPES;

@Component({
  selector: 'app-expansion-upsells-company-upsell',
  templateUrl: './company-upsell.component.html',
  styleUrls: ['./company-upsell.component.scss'],
})
export class ExpansionUpsellsCompanyUpsellComponent {
  @Output() onShowUpsell: EventEmitter<void> = new EventEmitter<void>();
  public showUpsell: boolean = false;

  public readonly crossIcon: string = CrossUI;

  public header?: SafeHtml;
  public body?: SafeHtml;
  public assetURL?: string;
  public assetTransform: number = 0;
  public assetMarginTop: string = '0px';

  public CTAs: IUpsellCTA[] = [];
  private industry: string;
  private upsellType: UpsellType = EXPANSION_UPSELL_TYPES.COMPANY_PAGE as UpsellType;

  constructor(
    private service: ExpansionUpsellService,
    private sanitzer: DomSanitizer,
    private featureUpgradeService: FeatureUpgradeService,
    private message: NzMessageService,
    private session: Session,
  ) { }

  public ngOnInit() {
    if (this.session.isDriver() || !this.service.isFeatureFlagEnabled()) return;
    this.service.getIndustry().subscribe((segments: string[]) => {
      const config: IExpansionUpsellsData = this.service.getDynamicConfig();
      const upsellIndustries: Set<string> = new Set(config.upsells.map((conf: ExpansionUpsellConfig) => conf.industry));

      const matchingIndustries: string[] = segments.filter(segment => upsellIndustries.has(segment));

      if (matchingIndustries.length === 1) {
        this.industry = matchingIndustries[0];
        this.initUpsell();
      }
    });
  }

  private initUpsell(): void {
    if (this.service.shouldShowAGUpsell(this.upsellType, this.industry)) {
      const upsellConfig = this.service.getUpsellConfig(
        this.upsellType,
        this.industry,
      ) as IExpansionUpsellsCompanyPageUpsell;
      if (upsellConfig) {
        this.setUpUpsell(upsellConfig);
        this.service.markUpsellAsShown(this.upsellType, this.industry);
        this.service.trackEvent(this.upsellType, this.industry, null, 'View');
        this.onShowUpsell.emit();
        this.showUpsell = true;
      }
    }
  }

  private setUpUpsell(upsellConfig: IExpansionUpsellsCompanyPageUpsell): void {
    this.CTAs = upsellConfig.cta;

    this.assetURL = upsellConfig.image.url;
    this.assetTransform = upsellConfig.image.transform;
    this.assetMarginTop = upsellConfig.image['margin-top'];

    this.header = this.sanitzer.sanitize(
      SecurityContext.HTML,
      upsellConfig.heading[this.service.getUserLanguage()],
    );
    this.body = this.sanitzer.sanitize(
      SecurityContext.HTML,
      upsellConfig.description[this.service.getUserLanguage()],
    );
  }

  public getCTAText(cta: IUpsellCTA): string {
    return cta.text[this.service.getUserLanguage()];
  }

  public handleCTAClick(cta: IUpsellCTA): void {
    if (cta.action.type === CTA_ACTION_TYPES.generateLead) {
      this.featureUpgradeService.genericUpsellWithCustomProps(
        cta.action.campaign_name,
        cta.text[this.service.getUserLanguage()],
        cta.action.offer_type,
        `${cta.action.utm_content}-A`,
        cta.action.product_interest,
      ).subscribe(() => {
        this.showUpsell = false;
        this.service.markUpsellAsCompleted(this.upsellType, this.industry);
        this.service.trackEvent(this.upsellType, this.industry, null, cta);
        this.message.success($localize`Thank you for your interest. We will reach out to you shortly.`);
      });
    }
    if (cta.action.type === CTA_ACTION_TYPES.redirect) {
      this.featureUpgradeService.genericUpsellWithCustomProps(
        cta.action.campaign_name,
        cta.text[this.service.getUserLanguage()],
        cta.action.offer_type,
        `${cta.action.utm_content}-A`,
        cta.action.product_interest,
      ).subscribe(() => {
        window.open(cta.action.route, '_blank');
      });
    }
  }

  public dismissUpsell(): void {
    this.showUpsell = false;
    this.service.markUpsellAsDismissed(this.upsellType, this.industry);
    this.service.trackEvent(this.upsellType, this.industry, null, 'Dismiss');
  }
}